import React, { useEffect, useRef, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { useAppDispatch } from '../../../../../store';
import { useSelector } from 'react-redux';
import { DLOPercentageOfWeeklyDSDVolumeTable } from '../../../components/Tables';
import DSDVolumeZoneDistributionByDay from '../../../components/Tables/DSDVolumeZoneDistributionByDay';
import NonDSDVolumeZoneDistributionByDay from '../../../components/Tables/NonDSDVolumeZoneDistributionByDay';
import LoadingTableContainer from './LoadingTableContainer';
import { 
  setActualDSDTableData, 
  setActualNONDSDTableData, 
  setActualDSDZoneTableData, 
  setActualNONDSDZoneTableData,
  setActiveContext 
} from '../../../../../store/slices/dloSlice';
import { transformDailyPercentageData, transformDailyCasesData, transformZoneDistributionData } from '../utils/dataTransformers';
import { 
  distributeWeeklyOverrideToZones, 
  recalculateWeeklyFromZones 
} from '../../../../../store/thunks/actualVolumeSync';
import ActualVolumeSyncDebugger from './ActualVolumeSyncDebugger';
import { toBackendWeek } from '../../../utils/weekTransforms';

interface ActualVolumeTabProps {
  // Data
  dloScheduleSetting: any;
  actualDSDTableData: any[];
  actualNONDSDTableData: any[];
  actualDSDZoneTableData: any[];
  actualNONDSDZoneTableData: any[];
  
  // Loading states
  actualDSDUpdateLoading: boolean;
  actualNONDSDUpdateLoading: boolean;
  actualDSDZoneUpdateLoading: boolean;
  actualNONDSDZoneUpdateLoading: boolean;
  
  // Saved states
  actualDSDZoneSaved: boolean;
  actualNONDSDZoneSaved: boolean;
  
  // Handlers
  handlers: any;
  
  // Styles
  styles: any;
}

const ActualVolumeTab: React.FC<ActualVolumeTabProps> = ({
  dloScheduleSetting,
  actualDSDTableData,
  actualNONDSDTableData,
  actualDSDZoneTableData,
  actualNONDSDZoneTableData,
  actualDSDUpdateLoading,
  actualNONDSDUpdateLoading,
  actualDSDZoneUpdateLoading,
  actualNONDSDZoneUpdateLoading,
  actualDSDZoneSaved,
  actualNONDSDZoneSaved,
  handlers,
  styles,
}) => {
  const dispatch = useAppDispatch();

  // Selectors for period/week logic
  const selectCreateSchedulePeriod = useSelector((state: any) => state.dlo.createSchedule.period);
  console.log('schedule period:', selectCreateSchedulePeriod);
  const selectCreateScheduleWeek = useSelector((state: any) => state.dlo.createSchedule.week);
  console.log('schedule week:', selectCreateScheduleWeek);
  const periodWeek = useSelector((state: any) => state.appData.periodWeek);
  console.log('Current period/week:', periodWeek?.data);
  // Determine if selected period/week is current period/week
  const isCurrentPeriodWeek = (
    selectCreateSchedulePeriod === periodWeek?.data?.period &&
    selectCreateScheduleWeek === toBackendWeek(periodWeek?.data?.period,periodWeek?.data?.week)
  );
  console.log('isCurrentPeriodWeek:', isCurrentPeriodWeek);
  // Prevent infinite sync loops
  const isSyncInProgress = useRef(false);
  
  // Debounce timers to prevent sync during typing
  const weeklyDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const zoneDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Track previous weekly data to detect which day changed
  const previousWeeklyData = useRef<any[]>(actualDSDTableData);
  
  // Track if initial sync has been triggered to prevent duplicate syncs
  const initialSyncTriggered = useRef(false);

  // Immediate sync function for weekly changes
  const performWeeklySync = useCallback((data: any[]) => {
    // Prevent recursive sync
    if (isSyncInProgress.current) {
      console.log('Sync in progress, skipping weekly->zone sync to prevent loop');
      return;
    }
    
    // Use setTimeout to ensure Redux state is updated first
    setTimeout(() => {
      // Extract location and period info from multiple possible sources
      let locationInfo = null;
      
      // First try to get from the weekly table data itself
      if (data.length > 0 && data[0]['Loc ID']) {
        locationInfo = {
          locId: data[0]['Loc ID'],
          yr: data[0].YR,
          prd: data[0].PRD,
          wk: data[0].WK
        };
        console.log('Location info from weekly table data:', locationInfo);
      }
      // Fallback to API data
      else if (dloScheduleSetting.scheduleSettingsActualDaily?.data?.length > 0) {
        const apiData = dloScheduleSetting.scheduleSettingsActualDaily.data[0];
        locationInfo = {
          locId: apiData['Loc ID'],
          yr: apiData.YR,
          prd: apiData.PRD,
          wk: apiData.WK
        };
        console.log('Location info from API data:', locationInfo);
      }
      // Last fallback to zone table data
      else if (actualDSDZoneTableData.length > 0 && actualDSDZoneTableData[0]['Loc ID']) {
        locationInfo = {
          locId: actualDSDZoneTableData[0]['Loc ID'],
          yr: actualDSDZoneTableData[0].YR,
          prd: actualDSDZoneTableData[0].PRD,
          wk: actualDSDZoneTableData[0].WK
        };
        console.log('Location info from zone table data:', locationInfo);
      }
      
      if (locationInfo && data.length > 0) {
        console.log('Weekly sync location info:', locationInfo);
        
        // Set sync flag to prevent recursion
        isSyncInProgress.current = true;
        
        // Day mapping for all paths
        const dayMapping: { [key: string]: number } = {
          'sun': 1, 'mon': 2, 'tue': 3, 'wed': 4, 'thu': 5, 'fri': 6, 'sat': 7
        };
        
        // Detect which day(s) changed by comparing with previous data
        const weeklyRow = data[0];
        const previousRow = previousWeeklyData.current?.[0];
        
        if (weeklyRow?.percentages && previousRow?.percentages) {
          // Only process days that actually changed
          Object.entries(weeklyRow.percentages).forEach(([dayKey, dayData]: [string, any]) => {
            const dayNum = dayMapping[dayKey];
            const currentOverride = parseFloat(dayData?.override || '0');
            const previousOverride = parseFloat(previousRow.percentages[dayKey]?.override || '0');
            
            // Check if this specific day changed
            if (currentOverride !== previousOverride) {
              console.log(`Day ${dayKey} (${dayNum}) changed from ${previousOverride} to ${currentOverride}`);
              
              // Always distribute, even when override is 0, to properly sync zone values
              if (dayNum !== undefined) {
                console.log('Dispatching distributeWeeklyOverrideToZones for:', { ...locationInfo, dayNum, overrideValue: currentOverride });
                // Pass the override value directly to the thunk (including 0 values)
                dispatch(distributeWeeklyOverrideToZones({
                  ...locationInfo,
                  dayNum,
                  overrideValue: currentOverride // Add the override value directly (can be 0)
                }));
              }
            } else {
              console.log(`Day ${dayKey} unchanged: ${currentOverride}`);
            }
          });
        } else {
          // First load or no previous data - sync all days
          console.log('No previous data for comparison, syncing all days');
          Object.entries(weeklyRow.percentages).forEach(([dayKey, dayData]: [string, any]) => {
            const dayNum = dayMapping[dayKey];
            const overrideValue = parseFloat(dayData?.override || '0');
            
            console.log(`Processing day ${dayKey} (${dayNum}):`, dayData, 'Override:', overrideValue);
            
            // Always distribute, even when override is 0, to properly sync zone values
            if (dayNum !== undefined) {
              console.log('Dispatching distributeWeeklyOverrideToZones for:', { ...locationInfo, dayNum, overrideValue });
              // Pass the override value directly to the thunk (including 0 values)
              dispatch(distributeWeeklyOverrideToZones({
                ...locationInfo,
                dayNum,
                overrideValue // Add the override value directly (can be 0)
              }));
            }
          });
        }
        
        // Update previous data reference for next comparison
        previousWeeklyData.current = data;
        
        // Clear sync flag after a delay longer than debounce timeout
        setTimeout(() => {
          isSyncInProgress.current = false;
          console.log('Weekly sync flag cleared');
        }, 600); // Longer than debounce timeout (200ms)
      } else {
        console.warn('No location info available for weekly sync', { 
          dataLength: data.length, 
          hasApiData: !!dloScheduleSetting.scheduleSettingsActualDaily?.data,
          hasZoneData: actualDSDZoneTableData.length > 0
        });
      }
    }, 100); // Small delay to ensure state update
  }, [dloScheduleSetting, actualDSDZoneTableData, dispatch]);

  // Immediate sync function for zone changes
  const performZoneSync = useCallback((data: any[]) => {
    // Prevent recursive sync
    if (isSyncInProgress.current) {
      console.log('Sync in progress, skipping zone->weekly sync to prevent loop');
      return;
    }
    
    // Use setTimeout to ensure Redux state is updated first
    setTimeout(() => {
      // Extract location and period info from multiple possible sources
      let locationInfo = null;
      
      // First try to get from zone table data
      if (data.length > 0 && data[0]['Loc ID']) {
        locationInfo = {
          locId: data[0]['Loc ID'],
          yr: data[0].YR,
          prd: data[0].PRD,
          wk: data[0].WK
        };
        console.log('Location info from zone table data:', locationInfo);
      } 
      // Fallback to API data
      else if (dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data?.length > 0) {
        const apiData = dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD.data[0];
        locationInfo = {
          locId: apiData['Loc ID'],
          yr: apiData.YR,
          prd: apiData.PRD,
          wk: apiData.WK
        };
        console.log('Location info from zone API data:', locationInfo);
      }
      // Last fallback to weekly table data
      else if (actualDSDTableData.length > 0 && actualDSDTableData[0]['Loc ID']) {
        locationInfo = {
          locId: actualDSDTableData[0]['Loc ID'],
          yr: actualDSDTableData[0].YR,
          prd: actualDSDTableData[0].PRD,
          wk: actualDSDTableData[0].WK
        };
        console.log('Location info from weekly table data:', locationInfo);
      }
      
      if (locationInfo) {
        console.log('Zone sync location info:', locationInfo);
        
        // Set sync flag to prevent recursion
        isSyncInProgress.current = true;
        
        // Zone table data doesn't have day_num - need to recalculate for all days
        // since we don't know which specific day was changed
        const allDays = [1, 2, 3, 4, 5, 6, 7]; // sun, mon, tue, wed, thu, fri, sat
        console.log('Processing days for recalculation:', allDays);
        
        // Trigger recalculation for each day
        allDays.forEach(dayNum => {
          console.log('Dispatching recalculateWeeklyFromZones for day:', dayNum, locationInfo);
          dispatch(recalculateWeeklyFromZones({
            ...locationInfo,
            dayNum
          }));
        });
        
        // Clear sync flag after a delay longer than debounce timeout
        setTimeout(() => {
          isSyncInProgress.current = false;
          console.log('Zone sync flag cleared');
        }, 600); // Longer than debounce timeout (200ms)
      } else {
        console.warn('No location info available for zone sync', {
          dataLength: data.length,
          hasApiData: !!dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data,
          hasWeeklyData: actualDSDTableData.length > 0
        });
      }
    }, 100); // Small delay to ensure state update
  }, [dloScheduleSetting, actualDSDTableData, dispatch]);

  // Debounced sync function for weekly changes
  const debouncedWeeklySync = useCallback((data: any[]) => {
    if (weeklyDebounceTimer.current) {
      clearTimeout(weeklyDebounceTimer.current);
    }
    
    weeklyDebounceTimer.current = setTimeout(() => {
      console.log('Debounced Weekly Sync triggered after user finished typing');
      // Double-check sync flag before executing
      if (!isSyncInProgress.current) {
        performWeeklySync(data);
      } else {
        console.log('Sync already in progress, skipping debounced weekly sync');
      }
    }, 200); // Wait 200ms after user stops typing - much more responsive
  }, [performWeeklySync]);

  // Debounced sync function for zone changes
  const debouncedZoneSync = useCallback((data: any[]) => {
    if (zoneDebounceTimer.current) {
      clearTimeout(zoneDebounceTimer.current);
    }
    
    zoneDebounceTimer.current = setTimeout(() => {
      console.log('Debounced Zone Sync triggered after user finished typing');
      // Double-check sync flag before executing
      if (!isSyncInProgress.current) {
        performZoneSync(data);
      } else {
        console.log('Sync already in progress, skipping debounced zone sync');
      }
    }, 200); // Wait 200ms after user stops typing - much more responsive
  }, [performZoneSync]);

  // Set active context when component mounts
  useEffect(() => {
    dispatch(setActiveContext('ActualVolumeTab'));
    
    // Initialize previous weekly data reference on mount
    if (actualDSDTableData && actualDSDTableData.length > 0) {
      previousWeeklyData.current = actualDSDTableData;
      console.log('Initialized previousWeeklyData on mount:', actualDSDTableData);
    }
    
    // Cleanup when component unmounts
    return () => {
      dispatch(setActiveContext(null));
      
      // Clear any pending timers
      if (weeklyDebounceTimer.current) {
        clearTimeout(weeklyDebounceTimer.current);
      }
      if (zoneDebounceTimer.current) {
        clearTimeout(zoneDebounceTimer.current);
      }
    };
  }, [dispatch]);

  // Trigger initial sync when both API data and table data are available
  useEffect(() => {
    const apiZoneData = dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data || [];
    const apiWeeklyData = dloScheduleSetting.scheduleSettingsActualDaily?.data || [];
    
    // Only sync once when:
    // 1. Weekly table data exists
    // 2. Zone API data exists (indicating zone table should initialize)
    // 3. Zone table data is still empty (hasn't initialized yet)
    // 4. Not already syncing
    // 5. Haven't triggered initial sync yet
    if (
      actualDSDTableData.length > 0 &&
      apiZoneData.length > 0 &&
      apiWeeklyData.length > 0 &&
      actualDSDZoneTableData.length === 0 &&
      !isSyncInProgress.current &&
      !initialSyncTriggered.current
    ) {
      console.log('Both API data available - performing initial weekly->zone sync');
      initialSyncTriggered.current = true;
      performWeeklySync(actualDSDTableData);
    }
  }, [actualDSDTableData, actualDSDZoneTableData]);

  // Initialize table data from API data when it arrives
  useEffect(() => {
    const apiWeeklyData = dloScheduleSetting.scheduleSettingsActualDaily?.data || [];
    
    // Check if we need to initialize from API
    const needsInitialization = apiWeeklyData.length > 0 && (
      actualDSDTableData.length === 0 ||
      // Check if existing data is just default zeros (no real API data loaded yet)
      (actualDSDTableData.length > 0 && actualDSDTableData[0]?.percentages && 
       Object.values(actualDSDTableData[0].percentages).every((day: any) => 
         parseFloat(day?.override || '0') === 0 && parseFloat(day?.default || '0') === 0
       ))
    );
    
    if (needsInitialization) {
      console.log('Initializing actualDSDTableData from API data');
      const transformedData = transformDailyCasesData(apiWeeklyData);
      dispatch(setActualDSDTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsActualDaily?.data, dispatch]);

  // Initialize zone table data from API data when it arrives
  useEffect(() => {
    const apiZoneData = dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data || [];
    
    // Check if we need to initialize from API
    const needsInitialization = apiZoneData.length > 0 && (
      actualDSDZoneTableData.length === 0 ||
      // Check if existing data is just default zeros
      (actualDSDZoneTableData.length > 0 && actualDSDZoneTableData[0]?.percentages &&
       Object.values(actualDSDZoneTableData[0].percentages).every((day: any) => 
         parseFloat(day?.override || '0') === 0 && parseFloat(day?.default || '0') === 0
       ))
    );
    
    if (needsInitialization) {
      console.log('Initializing actualDSDZoneTableData from API data');
      const transformedData = transformZoneDistributionData(apiZoneData, 'DSD');
      dispatch(setActualDSDZoneTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data, dispatch]);

  // Trigger sync after both tables are initialized
  useEffect(() => {
    // Trigger sync when:
    // 1. Both table data are populated (not empty)
    // 2. Not already syncing
    // 3. Haven't triggered initial sync yet
    if (
      actualDSDTableData.length > 0 &&
      actualDSDZoneTableData.length > 0 &&
      !isSyncInProgress.current &&
      !initialSyncTriggered.current
    ) {
      console.log('Both tables initialized - performing initial weekly->zone sync');
      initialSyncTriggered.current = true;
      // Small delay to ensure both tables are fully rendered
      setTimeout(() => {
        performWeeklySync(actualDSDTableData);
      }, 100);
    }
  }, [actualDSDTableData.length, actualDSDZoneTableData.length]);

  // Enhanced onChange handler for Weekly DSD Volume that triggers zone sync
  const handleWeeklyDSDChange = (data: any[]) => {
    console.log('Weekly DSD Change triggered:', data);
    dispatch(setActualDSDTableData(data));
    
    // Check sync flag and cancel pending timers if sync is in progress
    if (!isSyncInProgress.current) {
      console.log('Triggering debounced weekly sync');
      debouncedWeeklySync(data);
    } else {
      console.log('Skipping debounced weekly sync - sync in progress');
      // Cancel any pending debounced syncs
      if (weeklyDebounceTimer.current) {
        clearTimeout(weeklyDebounceTimer.current);
        weeklyDebounceTimer.current = null;
      }
      if (zoneDebounceTimer.current) {
        clearTimeout(zoneDebounceTimer.current);
        zoneDebounceTimer.current = null;
      }
    }
  };

  // Enhanced onChange handler for Zone Distribution that triggers weekly recalc
  const handleZoneDistributionChange = (data: any[]) => {
    console.log('Zone Distribution Change triggered:', data);
    dispatch(setActualDSDZoneTableData(data));
    
    // Check sync flag and cancel pending timers if sync is in progress
    if (!isSyncInProgress.current) {
      console.log('Triggering debounced zone sync');
      debouncedZoneSync(data);
    } else {
      console.log('Skipping debounced zone sync - sync in progress');
      // Cancel any pending debounced syncs
      if (weeklyDebounceTimer.current) {
        clearTimeout(weeklyDebounceTimer.current);
        weeklyDebounceTimer.current = null;
      }
      if (zoneDebounceTimer.current) {
        clearTimeout(zoneDebounceTimer.current);
        zoneDebounceTimer.current = null;
      }
    }
  };

  // Wrapper for weekly DSD save that also saves zone distribution
  const handleSaveWeeklyDSDWithSync = async () => {
    console.log('Saving Weekly DSD and synced Zone Distribution');
    // Save weekly table
    await handlers.handleSaveActualDSDData();
    // Also save zone table since they're synced
    await handlers.handleSaveActualDSDZoneDistribution();
  };

  // Wrapper for zone distribution save that also saves weekly DSD
  const handleSaveZoneDistributionWithSync = async () => {
    console.log('Saving Zone Distribution and synced Weekly DSD');
    // Save zone table
    await handlers.handleSaveActualDSDZoneDistribution();
    // Also save weekly table since they're synced
    await handlers.handleSaveActualDSDData();
  };

  return (
    <View style={localStyles.tabPanel}>
      {/* Debug Component - Only in Development */}
      {/* <ActualVolumeSyncDebugger /> */}
      
      {/* 1. Percentage of Weekly DSD Volume - Actual Data */}
      {dloScheduleSetting.actualDailyLoading ? (
        <LoadingTableContainer title="Daily DSD Volume (Actual)" style={styles.tableSpacing} />
      ) : (
        <DLOPercentageOfWeeklyDSDVolumeTable
          data={transformDailyCasesData(dloScheduleSetting.scheduleSettingsActualDaily?.data || [])}
          value={actualDSDTableData}
          onChange={handleWeeklyDSDChange}
          onSave={handleSaveWeeklyDSDWithSync}
          onRowSave={(rowId) => console.log('Actual DSD Percentage row save action:', rowId)}
          headerTitle="WEEKLY DSD VOLUME (ACTUAL)"
          type="DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={actualDSDUpdateLoading}
          displayAsPercentage={false}
          disabled={!isCurrentPeriodWeek}
        />
      )}

        {dloScheduleSetting.actualZoneDistributionDSDLoading ? (
          <LoadingTableContainer title="DSD Zone Volume Distribution By Day (Actual)" style={styles.tableSpacing} />
        ) : (
          <DSDVolumeZoneDistributionByDay
            data={actualDSDZoneTableData.length > 0 ? [] : (dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data || [])}
            value={actualDSDZoneTableData}
            onChange={handleZoneDistributionChange}
            onAddZone={() => console.log('Actual DSD Add zone action')}
            onSave={handleSaveWeeklyDSDWithSync}
            title="DSD ZONE VOLUME DISTRIBUTION BY DAY (ACTUAL)"
            type="DSD"
            context="schedule-settings"
            style={styles.tableSpacing}
            isLoading={actualDSDZoneUpdateLoading}
            isSaved={actualDSDZoneSaved}
            {...({ displayAsPercentage: false } as any)}
            isActualVolumeTab={true}
            disabled={!isCurrentPeriodWeek}
          />
        )}

        {/* 3. Percentage of Weekly NON DSD Volume - Actual Data */}
      {/* Commenting out weekly NONDSD Volume section in Location & Schedule settings */}
      {/* {dloScheduleSetting.actualDailyNONDSDLoading ? (
        <LoadingTableContainer title="Percentage of Weekly NON DSD Volume (Actual)" style={styles.tableSpacing} />
      ) : (
        <DLOPercentageOfWeeklyDSDVolumeTable
          data={transformDailyPercentageData(dloScheduleSetting.scheduleSettingsActualDailyNONDSD?.data || [])}
          value={actualNONDSDTableData}
          onChange={(data) => dispatch(setActualNONDSDTableData(data))}
          onSave={handlers.handleSaveActualNONDSDData}
          onRowSave={(rowId) => console.log('Actual NON-DSD Percentage row save action:', rowId)}
          onRowClear={(rowId) => console.log('Actual NON-DSD Percentage row clear action:', rowId)}
          onRowDelete={(rowId) => console.log('Actual NON-DSD Percentage row delete action:', rowId)}
          headerTitle="PERCENTAGE OF WEEKLY NON DSD VOLUME (ACTUAL)"
          type="NON-DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={actualNONDSDUpdateLoading}
        />
      )} */}

      {/* 4. NON DSD Zone Volume Distribution By Day - Actual Data */}
      {dloScheduleSetting.actualZoneDistributionNONDSDLoading ? (
        <LoadingTableContainer title="NON DSD Zone Volume Distribution By Day (Actual)" style={styles.tableSpacing} />
      ) : (
        <NonDSDVolumeZoneDistributionByDay
          data={actualNONDSDZoneTableData.length > 0 ? [] : (dloScheduleSetting.scheduleSettingsActualZoneDistributionNONDSD?.data || [])}
          value={actualNONDSDZoneTableData}
          onChange={(data) => dispatch(setActualNONDSDZoneTableData(data))}
          onAddZone={() => console.log('Actual NON-DSD Add zone action')}
          onSave={handlers.handleSaveActualNONDSDZoneDistribution}
          title="NON DSD ZONE VOLUME DISTRIBUTION BY DAY (ACTUAL)"
          type="NON-DSD"
          context="schedule-settings"
          style={styles.tableSpacing}
          isLoading={actualNONDSDZoneUpdateLoading}
          isSaved={actualNONDSDZoneSaved}
          {...({ displayAsPercentage: false } as any)}
          disabled={!isCurrentPeriodWeek}
        />
      )}
    </View>
  );
};

const localStyles = StyleSheet.create({
  tabPanel: {
    flex: 1,
  },
});

export default ActualVolumeTab;
